import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useRect } from '@elfsight/lib-utils';
import { getBrightInvertedColor, useWidgetRect } from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';
import { useSettings } from '@wu/widget-core';
import classNames from 'classnames';
import { meta } from '../../../meta';
import {
  getContainedSize,
  getImageClickAction,
  getLayoutObjFromName
} from '../../helpers';
import { useImages } from '../images';
import { useLazyImageProps } from '../lazy-loading';
import { useLightbox } from '../lightbox';
import { Info } from './Info';
import { WatermarkLogo } from './WatermarkLogo';

const getTransformFilterStyles = (effect, type) => {
  switch (effect) {
    case 'shrink':
      return type === 'hover'
        ? 'transform: scale(1);'
        : 'transform: scale(1.1);';
    case 'zoomIn':
      return 'transform: scale(1.2);';
    case 'blur':
      return 'filter: blur(5px);';
    case 'grayscale':
      return 'filter: grayscale(1);';
    case 'darken':
      return 'filter: brightness(0.7);';
    case 'colorIn':
      return 'mix-blend-mode: luminosity;';
    default:
      return '';
  }
};

const getInitialOpacity = (show, hoverMode, position) => {
  if (!show) {
    return '';
  }

  if (
    position === 'belowImage' ||
    hoverMode === 'disappears' ||
    hoverMode === 'noChange'
  ) {
    return 'opacity: 1;';
  }

  if (hoverMode === 'appears') {
    return 'opacity: 0;';
  }

  return '';
};

export const ItemData = (props) => {
  const windowWidth = useWidgetRect()?.width;
  const { imageClassName, infoClassName, data, imageHeight, isDrag, id } =
    props;
  const imageRef = useRef(null);
  const { ref: imageBoundsRef, bounds: imageBounds } = useRect({
    withRevalidateOnResize: true
  });
  const { ref: containerBoundsRef, bounds: containerBounds } = useRect({
    withRevalidateOnResize: true
  });
  const mergedImageRef = useMergedRef(imageRef, imageBoundsRef);
  const watermarkWrapperMaxHeight = useMemo(() => {
    if (!imageBounds || !containerBounds) {
      return 0;
    }

    return imageBounds.bottom - containerBounds.top;
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [containerBounds.top, imageBounds.bottom]);
  const [imageFitSize, setImageFitSize] = useState({});
  const [resizeIsCrop, setResizeIsCrop] = useState(false);
  const [needToResize, setNeedToResize] = useState(false);
  const { setSourceLink, toggleLightbox } = useLightbox();
  const {
    imageInfoPosition,
    imageOverlayColor,
    imageTextColor,
    gridImageRatio,
    thumbnailsImageRatio,
    sliderImageRatio,
    gridImageResize,
    thumbnailsImageResize,
    sliderImageResize,
    imageElements,
    imageInfoOnHover,
    imageClickAction,
    openLinkInNewTab,
    imageShape,
    imageEffectOnHover,
    imageEffectByDefault,
    imageEffectColorOnHover,
    imageEffectColorByDefault,
    layout,
    imageCopyProtection,
    imageOrder,
    editMode
  } = useSettings();
  const { hasGoogleIntegration } = useImages();
  const currentImageClickAction = getImageClickAction(
    imageClickAction,
    hasGoogleIntegration
  );

  const { name } = getLayoutObjFromName(layout);
  const {
    title,
    description,
    link,
    alt,
    categories: currentCategoriesId
  } = data;

  const infoProps = {
    description,
    title,
    infoClassName,
    currentCategoriesId
  };

  const couldBeClicked = imageClickAction !== 'Nothing';

  const hasTitle = !!title && imageElements.includes('title');
  const hasDescription = !!description && imageElements.includes('description');
  const hasCategories =
    !!currentCategoriesId.length && imageElements.includes('categories');
  const shouldShowOverlay = hasTitle || hasDescription || hasCategories;

  useLayoutEffect(() => {
    setImageFitSize(getContainedSize(imageRef.current));

    setResizeIsCrop(
      (name === 'grid' && gridImageResize === 'crop') ||
        (name === 'thumbnails' && thumbnailsImageResize === 'crop') ||
        (name === 'slider' && sliderImageResize === 'crop')
    );
    setNeedToResize(
      name === 'grid' || name === 'thumbnails' || name === 'slider'
    );
  }, [
    gridImageRatio,
    thumbnailsImageRatio,
    sliderImageRatio,
    gridImageResize,
    thumbnailsImageResize,
    sliderImageResize,
    windowWidth,
    imageOrder,
    name
  ]);

  function checkCopyProtection(e) {
    if (imageCopyProtection) e.preventDefault();
  }

  function clickItemHandler() {
    if (!isDrag) {
      if (currentImageClickAction === 'popup') {
        setSourceLink(id + 1);
        toggleLightbox(true);
      }

      if (currentImageClickAction === 'link' && link) {
        const clickLink =
          link.includes('http') ||
          link.includes('mailto:') ||
          link.includes('tel:')
            ? link
            : `https://${link}`;
        const target =
          !editMode && openLinkInNewTab === false ? '_top' : '_blank';
        const win = window.open(clickLink, target);
        win.focus();
      }
    }
  }

  const imageTextColorStyle =
    imageTextColor ||
    (imageOverlayColor
      ? getBrightInvertedColor(imageOverlayColor, [
          'rgb(255, 255, 255)',
          'rgb(29, 33, 41)'
        ])
      : '');

  const { lazyLoaded, lazyImageVisibility } = useLazyImageProps(
    data.id,
    imageRef
  );
  const imageUrl = lazyLoaded ? data.image.url : null;

  const accessibilityProps = useMemo(() => {
    if (!couldBeClicked) {
      return {};
    }

    const ariaLabel =
      currentImageClickAction === 'popup'
        ? 'View full-size version of this image'
        : 'Visit linked content';

    return {
      'aria-label': ariaLabel,
      role: 'button',
      tabIndex: 0
    };
  }, [currentImageClickAction, couldBeClicked]);

  const isCorrectImagePosition =
    imageInfoPosition === 'belowImage' || imageInfoPosition === 'onImage';
  const hasContent = description || title || !!currentCategoriesId.length;
  const showInfoBlock =
    isCorrectImagePosition && hasContent && !!imageElements.length;

  return (
    <Container
      {...accessibilityProps}
      ref={containerBoundsRef}
      onClick={couldBeClicked ? clickItemHandler : undefined}
      $couldBeClicked={couldBeClicked}
      $shouldShowOverlay={shouldShowOverlay}
      $imageInfoOnHover={imageInfoOnHover}
      $imageInfoPosition={imageInfoPosition}
      $imageEffectOnHover={imageEffectOnHover}
      $imageEffectColorOnHover={imageEffectColorOnHover}
    >
      <ImageContainer
        className={classNames({
          [`${imageClassName}Container`]: imageClassName
        })}
        onTransitionEnd={() =>
          setImageFitSize(getContainedSize(imageRef.current))
        }
        $imageShape={imageShape}
        $imageInfoPosition={imageInfoPosition}
        $hasContent={hasContent}
      >
        <Image
          ref={mergedImageRef}
          onDragStart={(e) => e.preventDefault()}
          src={imageUrl}
          style={{ visibility: lazyImageVisibility }}
          alt={alt || title}
          className={imageClassName}
          onContextMenu={checkCopyProtection}
          $imageHeight={imageHeight}
          $layout={layout}
          $lazyImageVisibility={lazyImageVisibility}
          $imageEffectOnHover={imageEffectOnHover}
          $imageEffectByDefault={imageEffectByDefault}
        />

        <ImageBackground
          $imageShape={imageShape}
          $resizeIsCrop={resizeIsCrop}
          $needToResize={needToResize}
          $imageFitSize={imageFitSize}
          $imageEffectByDefault={imageEffectByDefault}
          $imageEffectColorByDefault={imageEffectColorByDefault}
        />
      </ImageContainer>

      <WatermarkLogo
        imageRef={imageRef}
        watermarkWrapperMaxHeight={watermarkWrapperMaxHeight}
        imageCopyProtection={imageCopyProtection}
        needWrapperSize={!resizeIsCrop && needToResize}
      />

      {showInfoBlock && (
        <InfoWrapper
          $position={imageInfoPosition}
          $textColor={imageTextColor}
          $textColorStyle={imageTextColorStyle}
          $overlayColor={imageOverlayColor}
          $layout={layout}
          $shouldShowOverlay={shouldShowOverlay}
          $imageInfoOnHover={imageInfoOnHover}
          $imageInfoPosition={imageInfoPosition}
        >
          <Info {...infoProps} />

          {imageInfoPosition === 'onImage' && (
            <Overlay
              $imageShape={imageShape}
              $overlayColor={imageOverlayColor}
              $resizeIsCrop={resizeIsCrop}
              $needToResize={needToResize}
              $imageFitSize={imageFitSize}
              $shouldShowOverlay={shouldShowOverlay}
              $imageInfoOnHover={imageInfoOnHover}
              $imageInfoPosition={imageInfoPosition}
            />
          )}
        </InfoWrapper>
      )}
    </Container>
  );
};

const Image = styled.img.attrs({
  className: `${meta.stylePrefix}-item-data-image`
})`
  width: 100% !important;
  height: 100% !important;
  user-select: none;
  transform: scale(1);
  filter: blur(0) grayscale(0) brightness(1);
  transition: 0.3s ease all;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  backface-visibility: hidden;

  ${({ $imageHeight }) =>
    $imageHeight &&
    `height: ${
      $imageHeight === 'auto'
        ? '100% !important;'
        : `${$imageHeight}px !important;`
    };`}

  ${({ $layout, $lazyImageVisibility }) =>
    $layout === 'panoramic' &&
    $lazyImageVisibility === 'unset' &&
    css`
      object-fit: cover;
      aspect-ratio: 2 / 1;
    `}

  ${({ $imageEffectByDefault, $imageEffectOnHover }) => {
    const effect = getTransformFilterStyles($imageEffectByDefault, 'default');

    if ($imageEffectOnHover === 'shrink') {
      return css`
        ${effect}
        transform: scale(1.1);
      `;
    }

    return effect;
  }}
`;

const ImageBackground = styled.div.attrs({
  className: `${meta.stylePrefix}-item-data-imageBackground`
})`
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 100%;
  background-color: transparent;
  transition: 0.3s ease background-color;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);

  border-radius: ${({ $imageShape }) => $imageShape}%;

  width: ${({ $resizeIsCrop, $needToResize, $imageFitSize }) =>
    $resizeIsCrop || !$needToResize ? '100%;' : `${$imageFitSize[0]}px;`};
  height: ${({ $resizeIsCrop, $needToResize, $imageFitSize }) =>
    $resizeIsCrop || !$needToResize ? '100%;' : `${$imageFitSize[1]}px;`};

  ${({ $imageEffectByDefault, $imageEffectColorByDefault }) =>
    $imageEffectByDefault === 'colorIn' &&
    `background-color: ${$imageEffectColorByDefault};`}
`;

const InfoWrapper = styled.div.attrs({
  className: `${meta.stylePrefix}-item-data-info`
})`
  position: relative;
  color: #111;
  transition: 0.3s ease all;
  z-index: 3;

  ${({ $shouldShowOverlay, $imageInfoOnHover, $imageInfoPosition }) =>
    getInitialOpacity(
      $shouldShowOverlay,
      $imageInfoOnHover,
      $imageInfoPosition
    )}

  ${({ $position, $textColor, $textColorStyle, $overlayColor, $layout }) => {
    if ($position === 'onImage') {
      return css`
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0 4px;
        text-align: center;
        ${($textColor || $overlayColor) && `color: ${$textColorStyle};`}
      `;
    }

    return css`
      text-align: left;
      ${$textColor && `color: ${$textColor};`}

      ${($layout === 'grid' ||
        $layout === 'masonry' ||
        $layout === 'horizontalMasonry') &&
      'margin-bottom: 10px;'}
    `;
  }}
`;

const Overlay = styled.div.attrs({
  className: `${meta.stylePrefix}-item-data-overlay`
})`
  position: absolute;
  opacity: 0;
  left: 50%;
  top: 50%;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 0;
  transition: 0.3s ease all;
  transform: translate(-50%, -50%);

  ${({ $shouldShowOverlay, $imageInfoOnHover, $imageInfoPosition }) =>
    getInitialOpacity(
      $shouldShowOverlay,
      $imageInfoOnHover,
      $imageInfoPosition
    )}

  border-radius: ${({ $imageShape }) => $imageShape}%;
  background-color: ${({ $overlayColor }) => $overlayColor};

  width: ${({ $resizeIsCrop, $needToResize, $imageFitSize }) =>
    $resizeIsCrop || !$needToResize ? '100%;' : `${$imageFitSize[0]}px;`};
  height: ${({ $resizeIsCrop, $needToResize, $imageFitSize }) =>
    $resizeIsCrop || !$needToResize ? '100%;' : `${$imageFitSize[1]}px;`};
`;

const Container = styled.div.attrs({
  className: `${meta.stylePrefix}-item-data-component`
})`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: 100%;

  ${({ $couldBeClicked }) => $couldBeClicked && 'cursor: pointer;'}

  & div:empty {
    display: block !important;
  }

  &:hover {
    ${Overlay},
    ${InfoWrapper} {
      ${({ $shouldShowOverlay, $imageInfoOnHover, $imageInfoPosition }) => {
        if (!$shouldShowOverlay) {
          return '';
        }

        if (
          $imageInfoOnHover === 'appears' ||
          $imageInfoOnHover === 'noChange' ||
          $imageInfoPosition === 'belowImage'
        ) {
          return 'opacity: 1;';
        }

        if ($imageInfoOnHover === 'disappears') {
          return 'opacity: 0;';
        }

        return '';
      }}
    }
  }

  &:hover ${ImageBackground} {
    ${({ $imageEffectOnHover, $imageEffectColorOnHover }) =>
      $imageEffectOnHover === 'colorIn' &&
      `background-color: ${$imageEffectColorOnHover};`}
  }

  &:hover ${Image} {
    transform: scale(1);
    filter: blur(0) grayscale(0) brightness(1);
    mix-blend-mode: normal;

    ${({ $imageEffectOnHover }) =>
      getTransformFilterStyles($imageEffectOnHover, 'hover')}
  }
`;

const ImageContainer = styled.div.attrs({
  className: `${meta.stylePrefix}-item-data-imageContainer`
})`
  overflow: hidden;
  display: flex;
  height: 100%;
  width: 100%;
  transition: 0.3s ease background-color;
  background-color: transparent;
  position: relative;

  border-radius: ${({ $imageShape }) => $imageShape}%;

  margin-top: 0;
  margin-bottom: ${({ $hasContent, $imageInfoPosition }) =>
    $hasContent && $imageInfoPosition !== 'onImage' ? '16px' : '0'};
`;
